/**
 * UltraAdminSupplierProductsPage
 * ──────────────────────────────
 * Ultra Admin, platform'a kayıtlı tüm supplier'ların (tedarikçi) eklediği
 * ürünleri burada TEK bir panelde görüntüler. Salt okunur — ürünlerin sahibi
 * tedarikçilerdir; Ultra Admin yalnızca moderasyon/izleme amacıyla inceler.
 *
 * Özellikler:
 *  • Arama (başlık / ürün kodu / açıklama)
 *  • Tedarikçi (tenant) dropdown filtresi — ilk önce o supplier'dan gelen ürünler yüklenir
 *  • Durum filtresi (Tümü / Aktif / Pasif)
 *  • Kategori filtresi
 *  • Detay kartı: supplier adı · başlık · kategori · kapak görseli · stok · fiyat · aktif/pasif rozeti
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Eye, EyeOff, Building2, Image as ImageIcon, ExternalLink } from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import { toast } from 'sonner';

export default function UltraAdminSupplierProductsPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  const [products, setProducts]         = useState([]);
  const [suppliers, setSuppliers]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [filterActive, setFilterActive] = useState('all');      // all | active | passive
  const [filterSupplier, setFilterSupplier] = useState('');     // tenant_id
  const [filterCategory, setFilterCategory] = useState('');

  // ── Auth guard ──
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!token) navigate('/giris?tab=ultra');
  }, [navigate]);

  // ── Load products + suppliers in parallel ──
  // (Manual axios call is preserved here — `useApi` does not yet handle
  //  parallel multi-fetch with conditional re-use. Migration uses the new
  //  `apiClient` so the auth header / 401 logic matches the rest of the app.)
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSupplier)             params.tenant_id = filterSupplier;
      if (filterCategory)             params.category  = filterCategory;
      if (filterActive === 'active')  params.is_active = true;
      if (filterActive === 'passive') params.is_active = false;
      if (search.trim())              params.search    = search.trim();

      const [pRes, sRes] = await Promise.all([
        apiClient.get('/ultra-admin/supplier-products', { params }),
        suppliers.length
          ? Promise.resolve({ data: suppliers })
          : apiClient.get('/ultra-admin/supplier-products/suppliers'),
      ]);
      setProducts(pRes.data || []);
      if (!suppliers.length) setSuppliers(sRes.data || []);
    } catch (e) {
      toast.error(tr ? 'Ürünler yüklenemedi.' : 'Failed to load products.');
    } finally {
      setLoading(false);
    }
    // suppliers is intentionally not in deps — we only fetch them once.
  }, [filterSupplier, filterActive, filterCategory, search, tr]); // eslint-disable-line

  useEffect(() => { fetchProducts(); /* eslint-disable-next-line */ }, [filterSupplier, filterActive, filterCategory]);

  const onSearchSubmit = (e) => { e.preventDefault(); fetchProducts(); };

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set).sort();
  }, [products]);

  const stats = useMemo(() => ({
    total:     products.length,
    active:    products.filter(p => p.is_active).length,
    passive:   products.filter(p => !p.is_active).length,
    suppliers: new Set(products.map(p => p.tenant_id)).size,
  }), [products]);

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar />
      <div className="flex-1 flex flex-col">
        <UltraAdminTopbar />

        <div className="px-6 pt-5">
          <PageIntro {...getPageIntro('supplier-products', language)} />
        </div>

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200 bg-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Package className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {tr ? 'Tedarikçi Ürünleri' : 'Supplier Products'}
                </h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  {tr
                    ? 'Tüm tedarikçiler tarafından eklenen ürünler — salt okunur genel bakış.'
                    : 'All products added by suppliers — read-only overview.'}
                </p>
              </div>
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-2 flex-wrap">
              <StatChip label={tr ? 'Toplam' : 'Total'}      value={stats.total}     color="gray"    testid="stat-total" />
              <StatChip label={tr ? 'Aktif' : 'Active'}      value={stats.active}    color="emerald" testid="stat-active" />
              <StatChip label={tr ? 'Pasif' : 'Passive'}     value={stats.passive}   color="rose"    testid="stat-passive" />
              <StatChip label={tr ? 'Tedarikçi' : 'Suppliers'} value={stats.suppliers} color="indigo"  testid="stat-suppliers" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-4 border-b border-gray-100 bg-white">
          <div className="flex flex-wrap gap-3">
            <form onSubmit={onSearchSubmit} className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tr ? 'Başlık / kod ile ara...' : 'Search title / code...'}
                className="w-full pl-10 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:ring-2 focus:ring-amber-300 focus:border-amber-400"
                data-testid="supplier-products-search-input"
              />
            </form>

            <select
              value={filterSupplier}
              onChange={(e) => setFilterSupplier(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-amber-300"
              data-testid="supplier-products-supplier-filter"
            >
              <option value="">{tr ? 'Tüm Tedarikçiler' : 'All Suppliers'}</option>
              {suppliers.map(s => (
                <option key={s.id} value={s.id}>
                  {s.company_name} · {s.product_count ?? 0} {tr ? 'ürün' : 'products'}
                </option>
              ))}
            </select>

            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-amber-300"
              data-testid="supplier-products-active-filter"
            >
              <option value="all">{tr ? 'Tüm Durumlar' : 'All Statuses'}</option>
              <option value="active">{tr ? 'Aktif' : 'Active'}</option>
              <option value="passive">{tr ? 'Pasif' : 'Passive'}</option>
            </select>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="text-sm rounded-xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-amber-300"
              data-testid="supplier-products-category-filter"
            >
              <option value="">{tr ? 'Tüm Kategoriler' : 'All Categories'}</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Product grid */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="text-center py-20 text-gray-400 text-sm">
              {tr ? 'Yükleniyor...' : 'Loading...'}
            </div>
          ) : products.length === 0 ? (
            <EmptyState tr={tr} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="supplier-products-grid">
              {products.map(p => <ProductCard key={p.id} p={p} tr={tr} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatChip({ label, value, color = 'gray', testid }) {
  const cls = {
    gray:    'bg-gray-100 text-gray-700 border-gray-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rose:    'bg-rose-50 text-rose-700 border-rose-200',
    indigo:  'bg-indigo-50 text-indigo-700 border-indigo-200',
  }[color];
  return (
    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border ${cls}`} data-testid={testid}>
      <span className="opacity-70 font-semibold">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function ProductCard({ p, tr }) {
  const hasPrice = p.daily_price != null || p.event_price != null;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col" data-testid={`supplier-product-card-${p.id}`}>
      {/* Cover */}
      <div className="aspect-[4/3] bg-gray-100 relative">
        {p.cover_url ? (
          <img
            src={p.thumb_url || p.cover_url}
            alt={p.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="h-10 w-10 text-gray-300" />
          </div>
        )}
        {/* Active/Passive pill */}
        <div className="absolute top-2 right-2">
          {p.is_active ? (
            <span className="text-[10px] font-bold bg-emerald-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
              <Eye className="h-3 w-3" /> {tr ? 'Aktif' : 'Active'}
            </span>
          ) : (
            <span className="text-[10px] font-bold bg-rose-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
              <EyeOff className="h-3 w-3" /> {tr ? 'Pasif' : 'Passive'}
            </span>
          )}
        </div>
        {p.image_count > 0 && (
          <div className="absolute bottom-2 left-2 text-[10px] font-bold bg-black/60 text-white px-2 py-0.5 rounded-full">
            {p.image_count} {tr ? 'görsel' : 'images'}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-700 uppercase tracking-wide mb-1">
          <Building2 className="h-3 w-3" />
          <span className="truncate">{p.supplier_name}</span>
          {p.supplier_country && <span className="text-gray-400">· {p.supplier_country}</span>}
        </div>
        <h3 className="font-bold text-gray-900 text-sm leading-tight mb-1 line-clamp-2">{p.title || '—'}</h3>
        {(p.category || p.sub_category) && (
          <p className="text-[11px] text-gray-500 uppercase tracking-wide">
            {[p.category, p.sub_category].filter(Boolean).join(' · ')}
          </p>
        )}
        {p.product_code && (
          <p className="text-[10px] text-gray-400 mt-1 font-mono">SKU: {p.product_code}</p>
        )}

        <div className="flex items-center justify-between gap-2 mt-auto pt-3 border-t border-gray-100">
          {hasPrice ? (
            <div className="text-xs">
              {p.daily_price != null && (
                <div className="font-bold text-gray-800">
                  {Number(p.daily_price).toLocaleString()} {p.currency}
                  <span className="text-gray-400 font-normal text-[10px]"> / {tr ? 'gün' : 'day'}</span>
                </div>
              )}
              {p.event_price != null && (
                <div className="text-[11px] text-gray-500">
                  {tr ? 'Etkinlik' : 'Event'}: {Number(p.event_price).toLocaleString()} {p.currency}
                </div>
              )}
            </div>
          ) : (
            <span className="text-[11px] text-gray-400 italic">{tr ? 'Fiyat yok' : 'No price'}</span>
          )}

          {p.stock_qty != null && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${p.stock_qty > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {p.stock_qty > 0 ? `${p.stock_qty} ${p.unit || (tr ? 'adet' : 'pcs')}` : (tr ? 'Tükendi' : 'Out')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ tr }) {
  return (
    <div className="text-center py-20">
      <div className="h-16 w-16 mx-auto rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
        <Package className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-bold text-gray-700 text-sm mb-1">
        {tr ? 'Henüz tedarikçi ürünü yok' : 'No supplier products yet'}
      </h3>
      <p className="text-xs text-gray-500 max-w-sm mx-auto">
        {tr
          ? 'Tedarikçiler kendi panellerinden ürün eklediklerinde burada otomatik olarak görünür.'
          : 'Products will appear here automatically as suppliers add them from their own panels.'}
      </p>
    </div>
  );
}
